import { useState } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';

// 로그인 폼 훅
export const useLoginForm = () => {
  const { login, googleLogin, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    id: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 실시간 유효성 검사
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // 에러 메시지 초기화
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    const emailTrim = formData.id.trim();

    if (!emailTrim) {
      errors.id = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      errors.id = '올바른 이메일 형식이 아닙니다.';
    }

    if (!formData.password) {
      errors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      errors.password = '비밀번호는 6자 이상이어야 합니다.';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await login(formData.id.trim(), formData.password);
      if (result.success) {
        navigate('/lecture');
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await googleLogin();
      if (result.success) {
        navigate('/lecture');
      }
    } catch (err) {
      console.error('Google login error:', err);
    }
  };

  const goToRegister = () => {
    navigate('/register');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return {
    formData,
    showPassword,
    validationErrors,
    loading,
    error,
    handleInputChange,
    handleSubmit,
    handleGoogleLogin,
    handleKeyPress,
    goToRegister,
    togglePasswordVisibility
  };
};

// 회원가입 폼 훅
export const useRegisterForm = () => {
  const { createAccount, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 실시간 유효성 검사
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // 에러 메시지 초기화
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    const emailTrim = formData.id.trim();
    if (!emailTrim) {
      errors.id = '이메일을 입력해주세요.';
    } else {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
      if (!emailOk) {
        errors.id = '올바른 이메일 형식이 아닙니다. (예: name@example.com)';
      }
    }

    if (!formData.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    } else if (formData.name.length < 2) {
      errors.name = '이름은 2자 이상이어야 합니다.';
    }

    const digitsOnly = formData.phone.replace(/\D/g, '');
    if (!digitsOnly) {
      errors.phone = '전화번호를 입력해주세요.';
    } else if (digitsOnly.length < 10 || digitsOnly.length > 11) {
      errors.phone = '전화번호는 숫자 10~11자리여야 합니다. (하이픈 생략 가능)';
    }

    if (!formData.password) {
      errors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      errors.password = '비밀번호는 6자 이상이어야 합니다.';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await createAccount(
        formData.id.trim(),
        formData.password,
        formData.name.trim(),
        formData.phone.replace(/\D/g, '')
      );
      if (result.success) {
        // 회원가입 성공 시 로그인 페이지로 이동
        navigate('/login');
      }
    } catch (error) {
      console.error('Register error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const toggleAdminCodeVisibility = () => {
    setShowAdminCode(!showAdminCode);
  };

  return {
    formData,
    showPassword,
    showConfirmPassword,
    showAdminCode,
    validationErrors,
    loading,
    error,
    handleInputChange,
    handleSubmit,
    handleKeyPress,
    goToLogin,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    toggleAdminCodeVisibility
  };
}; 